import { escapeHtml, localizedString } from '@pressreader/resources';

import { ARTICLE_BODY_ATTRIBUTE, COPY_LEGAL_NOTICE_CLASS, COPY_LEGAL_NOTICE_RESOURCE_KEY } from '../../constants';

// Re-exported so the reading surfaces in @pressreader/article-ui-common can tag themselves; the
// lib's public barrel exposes utils, not constants.
export { ARTICLE_BODY_ATTRIBUTE };

/**
 * Subtrees whose text counts as article content: the copy dialog's snippet
 * (components/dialog/copy/dialog.tsx) and the outer `<article>` of each reading surface, tagged with
 * `ARTICLE_BODY_ATTRIBUTE`. Both scopes cover title, byline and body, so a headline copied from the
 * page carries the notice exactly like one copied from the dialog. `ARTICLE_BODY_ATTRIBUTE` lists
 * the surfaces and the one element that carries the class but is deliberately left untagged.
 *
 * The scope matters in both directions. Too wide and the notice lands on copied share URLs, since
 * the legacy "Copy Link" button goes through clipboard.js and its execCommand path dispatches a real
 * `copy` event, or on feed teasers, which are out of scope. Too narrow and it misses a renderer.
 */
const ARTICLE_SELECTION_ROOTS = ['.copy-snippet', `[${ARTICLE_BODY_ATTRIBUTE}]`];

/** True when any range of the selection touches `node`. */
function selectionTouches(selection: Selection, node: Node): boolean {
    for (let i = 0; i < selection.rangeCount; i++) {
        if (selection.getRangeAt(i).intersectsNode(node)) {
            return true;
        }
    }
    return false;
}

/**
 * True when the selection is non-empty and touches article content.
 *
 * Every range is tested against every root, rather than resolving the first range's
 * `commonAncestorContainer`. A selection spanning the headline and the first paragraph, or running
 * from the article into the block below it, has a common ancestor ABOVE the article root, and
 * `closest()` only walks upwards, so an ancestor test misses the most ordinary copy on the page.
 */
function selectionIntersectsArticle(selection: Selection): boolean {
    if (selection.isCollapsed || !selection.rangeCount || !selection.toString().trim()) {
        return false;
    }

    const roots = Array.from(document.querySelectorAll(ARTICLE_SELECTION_ROOTS.join(',')));

    return roots.some(root => selectionTouches(selection, root));
}

/**
 * Normalizes a resolved notice to '' when the host has not supplied one.
 *
 * `localizedString` returns the key itself for a missing resource, which must never be shown in the
 * dialog nor land on the clipboard. Callers resolve the string themselves — the dialog through the
 * `useResources` hook so it re-renders when bundles load, this module imperatively at copy time.
 */
export function normalizeCopyLegalNotice(resolved: string | undefined): string {
    return !resolved || resolved === COPY_LEGAL_NOTICE_RESOURCE_KEY ? '' : resolved;
}

/**
 * The part of `node`'s text the selection actually covers, which is what the browser would put on
 * the clipboard for that node.
 *
 * Each range is clipped to the node's own bounds before serializing, so a selection starting halfway
 * through the node reports only the covered half.
 */
function selectedTextWithin(selection: Selection, node: Node): string {
    const bounds = document.createRange();
    bounds.selectNodeContents(node);

    let text = '';

    for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        if (!range.intersectsNode(node)) {
            continue;
        }

        const clipped = range.cloneRange();
        if (clipped.compareBoundaryPoints(Range.START_TO_START, bounds) < 0) {
            clipped.setStart(bounds.startContainer, bounds.startOffset);
        }
        if (clipped.compareBoundaryPoints(Range.END_TO_END, bounds) > 0) {
            clipped.setEnd(bounds.endContainer, bounds.endOffset);
        }

        text += clipped.toString();
    }

    return text;
}

/**
 * The selected text with whatever the dialog rendered of the notice removed, so the canonical
 * wording can be prepended exactly once.
 *
 * The dialog renders the notice as the first child of `.copy-snippet`, so Select-all carries it
 * whole and a drag beginning inside it carries a fragment — and a truncated notice is worse than no
 * notice at all. Both are dropped here rather than dedupe'd by comparing text, which would also
 * suppress the notice on prose that merely happened to open with the same wording.
 */
function selectionToText(selection: Selection): string {
    const text = selection.toString();
    const rendered = Array.from(document.querySelectorAll(`.${COPY_LEGAL_NOTICE_CLASS}`))
        .map(node => selectedTextWithin(selection, node))
        .join('');

    // The rendered notice leads the snippet, so it is a prefix of the selection. Anything else is
    // left alone: a duplicated notice is harmless, a mis-sliced one is not.
    const body = rendered && text.startsWith(rendered) ? text.slice(rendered.length) : text;

    return body.replace(/^\s+/, '');
}

/**
 * Serializes the selection to HTML so the copied markup survives a paste into Word/Outlook.
 *
 * `cloneContents` rebuilds an ancestor only when the range crosses that element's boundary. A
 * selection confined to one block returns its text and inline children with no wrapper at all, so
 * this can hand back a bare fragment. The caller wraps the result in a block for that reason.
 *
 * The ancestor a partial selection drops is lost: prose is not flat, and half a subheading
 * (TitleParagraph renders `<h2>`) or half a pull-quote (TextParagraph renders `<blockquote>` for
 * `Formatting.Quote`) pastes as plain text inside the wrapper. Reconstructing partial ancestors
 * would mean walking each range's start container up to its root, which is more than the notice
 * needs: the wrapper is only there to keep the body a block of its own, separate from the notice.
 */
function selectionToHtml(selection: Selection): string {
    const container = document.createElement('div');

    for (let i = 0; i < selection.rangeCount; i++) {
        container.appendChild(selection.getRangeAt(i).cloneContents());
    }

    // A partially selected ancestor is cloned with its attributes, so this drops a clipped notice as
    // well as a fully selected one.
    container.querySelectorAll(`.${COPY_LEGAL_NOTICE_CLASS}`).forEach(node => node.remove());

    return container.innerHTML;
}

function onCopy(event: ClipboardEvent): void {
    try {
        const { clipboardData } = event;
        if (!clipboardData) {
            return;
        }

        const selection = window.getSelection();
        if (!selection || !selectionIntersectsArticle(selection)) {
            return;
        }

        // Resolved per copy, not once at install time: resource bundles load asynchronously.
        const notice = normalizeCopyLegalNotice(localizedString(COPY_LEGAL_NOTICE_RESOURCE_KEY));
        if (!notice) {
            return;
        }

        const text = selectionToText(selection);

        event.preventDefault();
        clipboardData.setData('text/plain', text ? `${notice}\n\n${text}` : notice);
        // The body gets its own block: a selection inside a single paragraph clones as a bare
        // fragment, which would otherwise run straight on from the notice in a rich-text paste.
        clipboardData.setData('text/html', `<p>${escapeHtml(notice)}</p><div>${selectionToHtml(selection)}</div>`);
    } catch {
        // Never break copying: without preventDefault the browser performs its own default copy.
    }
}

let installed = false;

/**
 * Installs or removes the document-level `copy` interceptor. Idempotent, so the caller does not have
 * to track whether the listener is already attached.
 */
export function setCopyLegalNoticeEnabled(enabled: boolean): void {
    if (enabled === installed) {
        return;
    }

    if (enabled) {
        document.addEventListener('copy', onCopy);
    } else {
        document.removeEventListener('copy', onCopy);
    }

    installed = enabled;
}
